import type { Nivel } from '@/api/alianca';
import { BowArc } from '@/components/ui/BowArc';
import { Button } from '@/components/ui/Button';
import { Aurora, Grain } from '@/components/alianca/AliancaPrimitives';
import { NIVEL_VERSO, tierByKey } from '@/lib/alianca-tiers';

interface AliancaSubiuProps {
  fromNivel: Nivel;
  toNivel: Nivel;
  onDismiss: () => void;
}

/**
 * Overlay full-screen celebrativo · cliente subiu de nível.
 *
 * Disparado pelo LevelChangeToast ao detectar uma `LevelChange` com
 * `direction === 'up'`. Cobre a tela inteira; cliente dismissa via
 * "Aproveitar agora" (vai pra recarga) ou "Mais tarde".
 *
 * Design ref: design/redesign/bundle/app-bow-recarga/project/alianca-variants.jsx
 * (AliancaSubiu — overlay full-bleed na nova cor com versículo).
 */
export function AliancaSubiu({ fromNivel, toNivel, onDismiss }: AliancaSubiuProps) {
  const from = tierByKey(fromNivel);
  const to = tierByKey(toNivel);
  const ganho = to.pct - from.pct;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-hidden text-white"
      role="dialog"
      aria-modal="true"
    >
      <Aurora from={to.hex} to="#001B74" />
      <Grain opacity={0.1} />

      {/* Glow ring central */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: '24%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 320,
          height: 320,
          borderRadius: 999,
          background: `radial-gradient(circle, ${to.hex}cc 0%, ${to.hex}00 60%)`,
          filter: 'blur(20px)'
        }}
      />

      {/* Conteúdo centralizado */}
      <div className="relative flex flex-1 flex-col items-center justify-center px-8 text-center">
        <BowArc size={120} variant="white" />

        <div
          className="mt-8 text-[11px] font-semibold uppercase tracking-[0.16em]"
          style={{ opacity: 0.85 }}
        >
          Você subiu de nível
        </div>

        <div className="mt-3.5 flex items-center gap-3.5">
          <span
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: 22,
              opacity: 0.55,
              textDecoration: 'line-through',
              textDecorationThickness: '1px'
            }}
          >
            {from.label}
          </span>
          <span style={{ opacity: 0.5, fontSize: 22 }}>→</span>
          <span
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 56,
              letterSpacing: '-0.035em',
              lineHeight: 1
            }}
          >
            {to.label}
          </span>
        </div>

        <p
          className="mt-4 max-w-[280px] text-sm italic"
          style={{ opacity: 0.9, lineHeight: 1.5 }}
        >
          “{NIVEL_VERSO[to.key]}”
        </p>

        <div
          className="mt-7 rounded-[18px] border px-4 py-3.5"
          style={{
            background: 'rgba(255,255,255,0.16)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.18)'
          }}
        >
          <div className="flex items-center gap-3.5">
            <div>
              <div
                className="text-[10px] font-semibold uppercase tracking-[0.06em]"
                style={{ opacity: 0.75 }}
              >
                Você ganha agora
              </div>
              <div
                className="mt-0.5 flex items-baseline gap-1"
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 44,
                  letterSpacing: '-0.03em',
                  lineHeight: 1
                }}
              >
                {to.pct}%
              </div>
              <div className="mt-1 text-xs" style={{ opacity: 0.85 }}>
                de cashback ao recarregar
              </div>
            </div>
            <div
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold"
              style={{ background: '#FFFFFF', color: to.hex }}
            >
              ↑ +{ganho}%
            </div>
          </div>
        </div>

        <div className="mt-9 w-full max-w-[260px]">
          <Button variant="primary" size="lg" fullWidth onClick={onDismiss}>
            Aproveitar agora
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            className="mt-2.5 h-11 w-full border-0 bg-transparent text-[13px] font-medium text-white"
            style={{ opacity: 0.7 }}
          >
            Mais tarde
          </button>
        </div>
      </div>
    </div>
  );
}
